import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import CopyButton from '../CopyButton';

/**
 * "Who else used this IP?" — the rendering of one `GetIpPrevalenceUsers` response.
 *
 * ONE component, two surfaces: the Alerts page's IP Intelligence panel and the case IP
 * Addresses tab. Extracted from `AlertsPage.jsx` rather than copied, on the same parity
 * discipline as CLAUDE.md rule #13: this panel renders an AUTHORIZATION-SENSITIVE
 * response, and a second copy is a second place for the withholding and truncation
 * disclosures to be forgotten.
 *
 * 🔴 THREE GROUPS, AND THE THIRD IS THE ONE THAT MUST NOT BE DROPPED. The endpoint has
 * returned all three since v1.110.3:
 *
 *   1. `users` — same-tenant UPNs.
 *   2. `crossTenantUsers` — identities in OTHER tenants the caller is AUTHORIZED to read,
 *      each labelled with its tenant. Already scoped server-side from
 *      `Get-UserMspContext`; this only renders them.
 *   3. `withheldUserCount` / `withheldTenantCount` — COUNTS ONLY for tenants outside the
 *      caller's scope. Rendering the first two and silently omitting this would present a
 *      partial answer as a complete one, which is the whole reason the endpoint separates
 *      them.
 *
 * `crossTenantTruncated` is the cross-tenant 500-cap disclosure and is part of the
 * established contract — a capped list must never render as complete.
 *
 * The caller supplies `formatTs` so each surface keeps its own timezone handling (the
 * Alerts page and the case page resolve the zone differently), rather than this component
 * growing a third opinion about time.
 *
 * THIRD SURFACE (v1.196.0): the IP Search page (`SearchIpAccounts`), via `standalone`. A
 * search has no "home" tenant, so the server returns every readable account in
 * `crossTenantUsers` (each tenant-labelled) and `users` empty; `standalone` drops the
 * "this tenant" section and its wording. The withheld tail and the truncation line are the
 * SAME code for all three surfaces, which is why this is a mode and not a copy. Rows from
 * that endpoint also carry `interactiveCount` / `nonInteractiveCount`, shown when present.
 *
 * Standalone only (v1.197.0): a filter box (account, app, tenant, MSP) and grouping by
 * tenant, on by default. With more than one tenant the groups start collapsed; a filter
 * opens the groups it matches. The filter searches the accounts returned, so when the list
 * is capped the truncation line says so. The Alerts and case surfaces are unchanged.
 */

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// PowerShell's ConvertTo-Json unwraps a single-element array, so every list here is
// coerced. This has bitten the alert-family panels repeatedly.
const toArr = (v) => (Array.isArray(v) ? v : (v ? [v] : []));

// The literal `Not Available` placeholder Microsoft writes into UserId is not a user.
const notPlaceholder = (u) => u?.userId && u.userId.toLowerCase() !== 'not available';

const n = (v) => Number(v || 0).toLocaleString();

// "· 90 interactive, 1,755 non-interactive" — only for rows that carry the split.
function feedSplit(u) {
  if (u.interactiveCount == null && u.nonInteractiveCount == null) return null;
  const parts = [];
  if (u.interactiveCount) parts.push(`${n(u.interactiveCount)} interactive`);
  if (u.nonInteractiveCount) parts.push(`${n(u.nonInteractiveCount)} non-interactive`);
  return parts.length ? parts.join(', ') : null;
}

// Filter match: account, resolved app name/id, tenant name/id, MSP code.
function matchesQuery(u, q) {
  return [u.userId, u.displayName, u.appId, u.tenantName, u.tenantId, u.mspCode]
    .some((v) => v && String(v).toLowerCase().includes(q));
}

// The name a row displays: an app-only sign-in shows its resolved app name, else the UPN.
const displayLabel = (u) => (GUID_RE.test(u.userId) && u.displayName ? u.displayName : u.userId);
const tsOf = (v) => { const t = v ? Date.parse(v) : NaN; return Number.isNaN(t) ? null : t; };

// IP Search sort (v1.197.1). 'activity' is the server's order (most sign-ins first) and needs
// no comparator. The others sort within each tenant group (grouping happens after sorting
// and keeps row order). A missing timestamp always sorts last, in either direction.
const SORTS = [
  { id: 'activity', label: 'Most activity' },
  { id: 'account', label: 'Account' },
  { id: 'first', label: 'First seen' },
  { id: 'last', label: 'Last seen' },
];
function sortRows(rows, key, asc) {
  if (key === 'activity') return rows;
  const dir = asc ? 1 : -1;
  const cmp = key === 'account'
    ? (a, b) => dir * displayLabel(a).localeCompare(displayLabel(b), undefined, { sensitivity: 'base' })
    : (a, b) => {
      const f = key === 'first' ? 'firstSeenUtc' : 'lastSeenUtc';
      const x = tsOf(a[f]); const y = tsOf(b[f]);
      if (x == null || y == null) return (x == null) - (y == null);
      return dir * (x - y);
    };
  return [...rows].sort(cmp);
}

// One group per tenant, busiest (most accounts, then most events) first. Rows keep the
// order they arrive in (the chosen sort).
function groupByTenant(rows) {
  const map = new Map();
  for (const u of rows) {
    const key = u.tenantId || '(unknown tenant)';
    let g = map.get(key);
    if (!g) {
      g = { key, name: u.tenantName || u.tenantId || 'Unknown tenant', msp: u.mspCode || '', rows: [], events: 0, interactive: 0, nonInteractive: 0 };
      map.set(key, g);
    }
    g.rows.push(u);
    g.events += Number(u.eventCount || 0);
    g.interactive += Number(u.interactiveCount || 0);
    g.nonInteractive += Number(u.nonInteractiveCount || 0);
  }
  return [...map.values()].sort((a, b) => b.rows.length - a.rows.length || b.events - a.events || a.name.localeCompare(b.name));
}

export default function IpPrevalenceUsers({ data, ip, formatTs = null, className = '', standalone = false, windowLabel = '' }) {
  // Standalone-only view state. Declared before the early return (hook order).
  const [query, setQuery] = useState('');
  const [grouped, setGrouped] = useState(true);
  const [openState, setOpenState] = useState({});
  const [sortKey, setSortKey] = useState('activity');
  const [sortAsc, setSortAsc] = useState(true);

  if (!data) return null;

  const list = toArr(data.users).filter(notPlaceholder);
  const crossList = toArr(data.crossTenantUsers).filter(notPlaceholder);
  const windowDays = data.windowDays || 30;
  const when = windowLabel || `in the last ${windowDays}d`;

  const q = query.trim().toLowerCase();
  const shown = sortRows(q ? crossList.filter((u) => matchesQuery(u, q)) : crossList, sortKey, sortAsc);
  // Account reads A→Z first; dates read newest first.
  const chooseSort = (key) => { setSortKey(key); setSortAsc(key === 'account'); };
  const groups = standalone && grouped ? groupByTenant(shown) : [];
  const tenantCount = new Set(crossList.map((u) => u.tenantId || '')).size;
  // Collapsed by default when there is more than one tenant; a filter opens what it matched.
  const isOpen = (g) => openState[g.key] ?? (q ? true : groups.length === 1);
  const setAll = (open) => setOpenState(Object.fromEntries(groups.map((g) => [g.key, open])));

  const tenantBadge = (u) => (
    <span
      className="text-sky-300/90 text-[10px] px-1 py-0.5 rounded bg-sky-950/40 border border-sky-500/30"
      title={`Tenant ${u.tenantId}`}
    >
      {u.tenantName || u.tenantId}{u.mspCode ? ` · ${u.mspCode}` : ''}
    </span>
  );

  // A capped list must never render as complete.
  const truncationNote = data.crossTenantTruncated && (
    <div className="mt-1 text-amber-300/80 italic">
      {standalone
        ? `More accounts matched. Showing the ${n(data.limit || crossList.length)} with the most activity; narrow the date range to see the rest.${q ? ' The filter searches only these accounts.' : ''}`
        : 'Showing the top 500 cross-tenant users by activity.'}
    </div>
  );

  // A GUID-shaped UserId is a service-principal / app-only sign-in (an object id, not a
  // UPN): show the resolved app name as primary and keep the GUID as a muted sub.
  const renderUser = (u, extra = null) => {
    const isGuid = GUID_RE.test(u.userId);
    const showApp = isGuid && u.displayName;
    return (
      <li key={`${u.tenantId || 'self'}:${u.userId}`} className="group flex items-center gap-1.5 flex-wrap">
        <span
          className="text-gray-200 break-all"
          title={showApp ? `${u.userId}${u.appId ? ` · appId ${u.appId}` : ''}` : undefined}
        >
          {showApp ? u.displayName : u.userId}
        </span>
        {showApp && (
          <span className="text-purple-300/80 text-[10px] uppercase tracking-wide" title={u.userId}>app</span>
        )}
        <CopyButton text={u.userId} />
        {showApp && (
          <span className="text-gray-600 font-mono text-[10px] break-all">{u.userId}</span>
        )}
        {extra}
        <span className="text-gray-500">
          · {n(u.eventCount)} event{u.eventCount === 1 ? '' : 's'}
          {feedSplit(u) ? ` (${feedSplit(u)})` : ''}
          {standalone && u.firstSeenUtc && formatTs ? ` · first ${formatTs(u.firstSeenUtc)}` : ''}
          {u.lastSeenUtc && formatTs ? ` · last ${formatTs(u.lastSeenUtc)}` : ''}
        </span>
      </li>
    );
  };

  return (
    <div className={`rounded border border-gray-700 bg-gray-900/40 p-2 ${className}`}>
      {standalone ? (
        crossList.length === 0 && (
          <span className="text-gray-500 italic">
            No accounts used {ip} {when} in the tenants you can access.
          </span>
        )
      ) : list.length === 0 ? (
        <span className="text-gray-500 italic">
          No users in this tenant on {ip} in the last {windowDays}d.
        </span>
      ) : (
        <>
          <div className="text-gray-400 mb-1">
            {list.length} user{list.length === 1 ? '' : 's'} on {ip} (this tenant, last {windowDays}d)
          </div>
          <ul className="space-y-0.5">
            {list.map((u) => renderUser(u))}
          </ul>
        </>
      )}

      {/* Cross-tenant identities the caller is authorized to read (Alerts + case surfaces). */}
      {!standalone && crossList.length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-sky-500/30">
          <div className="text-sky-300/90 mb-1">
            {`${crossList.length} user${crossList.length === 1 ? '' : 's'} on ${ip} in other tenant${crossList.length === 1 ? '' : 's'} you can access`}
          </div>
          <ul className="space-y-0.5">
            {crossList.map((u) => renderUser(u, tenantBadge(u)))}
          </ul>
          {truncationNote}
        </div>
      )}

      {/* IP Search: every readable account, with a filter and grouping by tenant. */}
      {standalone && crossList.length > 0 && (
        <div>
          <div className="text-sky-300/90 mb-1">
            {`${n(crossList.length)} account${crossList.length === 1 ? '' : 's'} used ${ip} ${when}, in ${tenantCount} tenant${tenantCount === 1 ? '' : 's'} you can access`}
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <input
              className="input-field text-xs py-1 w-72"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by account, app, tenant or MSP"
              aria-label="Filter accounts"
            />
            <label className="inline-flex items-center gap-1.5 text-gray-400 cursor-pointer">
              <input type="checkbox" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} />
              Group by tenant
            </label>
            <label className="inline-flex items-center gap-1.5 text-gray-400">
              Sort
              <select
                className="input-field text-xs py-1"
                value={sortKey}
                onChange={(e) => chooseSort(e.target.value)}
                aria-label="Sort accounts"
              >
                {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              {sortKey !== 'activity' && (
                <button
                  type="button"
                  className="text-sky-400 hover:text-sky-300"
                  onClick={() => setSortAsc((v) => !v)}
                  title="Reverse the order"
                >
                  {sortKey === 'account'
                    ? (sortAsc ? 'A→Z' : 'Z→A')
                    : (sortAsc ? 'Oldest first' : 'Newest first')}
                </button>
              )}
            </label>
            {grouped && groups.length > 1 && (
              <span className="flex gap-2">
                <button type="button" className="text-sky-400 hover:text-sky-300" onClick={() => setAll(true)}>Expand all</button>
                <button type="button" className="text-sky-400 hover:text-sky-300" onClick={() => setAll(false)}>Collapse all</button>
              </span>
            )}
            {q && (
              <span className="text-gray-500">
                {n(shown.length)} of {n(crossList.length)} account{crossList.length === 1 ? '' : 's'} match
              </span>
            )}
          </div>

          {shown.length === 0 ? (
            <div className="text-gray-500 italic">No accounts match &ldquo;{query.trim()}&rdquo;.</div>
          ) : grouped ? (
            <div className="space-y-1">
              {groups.map((g) => {
                const open = isOpen(g);
                const split = feedSplit({ interactiveCount: g.interactive, nonInteractiveCount: g.nonInteractive });
                return (
                  <div key={g.key} className="rounded border border-gray-700/60">
                    <button
                      type="button"
                      onClick={() => setOpenState((s) => ({ ...s, [g.key]: !open }))}
                      className="w-full flex items-center gap-1.5 px-2 py-1 text-left hover:bg-gray-800/50"
                      aria-expanded={open}
                      title={`Tenant ${g.key}`}
                    >
                      {open ? <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400" />}
                      <span className="text-sky-300/90">{g.name}</span>
                      {g.msp && <span className="text-gray-500">· {g.msp}</span>}
                      <span className="text-gray-400 ml-1">
                        · {n(g.rows.length)} account{g.rows.length === 1 ? '' : 's'} · {n(g.events)} event{g.events === 1 ? '' : 's'}
                        {split ? ` (${split})` : ''}
                      </span>
                    </button>
                    {open && (
                      <ul className="space-y-0.5 px-2 pb-1.5 pl-7">
                        {g.rows.map((u) => renderUser(u))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <ul className="space-y-0.5">
              {shown.map((u) => renderUser(u, tenantBadge(u)))}
            </ul>
          )}
          {truncationNote}
        </div>
      )}

      {/* 🔴 The tail. Counts only, by design — these tenants are outside the caller's
          scope, and omitting the line would present a partial answer as a whole one. */}
      {data.withheldUserCount > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-gray-700/60 text-amber-300/80">
          + {data.withheldUserCount} user{data.withheldUserCount === 1 ? '' : 's'} in{' '}
          {data.withheldTenantCount} other tenant{data.withheldTenantCount === 1 ? '' : 's'} (UPNs
          withheld for tenant separation)
        </div>
      )}
    </div>
  );
}
